// Permisos de usuario que el frontend puede resolver por si mismo, leidos de
// `user.attributes` tal como los publica GET /session.
//
// Los flags de attributes llegan como boolean o como string segun el path de
// escritura al backend — mismo caso ya documentado en isPortalIncidentsUser
// (util/cad-incident-utils.js), por eso cada helper normaliza ambos formatos.

// Normaliza un flag de attributes a boolean estricto, o undefined si no viene.
function readFlag(value) {
  if (value === true || value === 'true') return true
  if (value === false || value === 'false') return false
  return undefined
}

/**
 * ¿El operador puede ver reportantes (RAPID-SOS)?
 *
 * Paridad con el mapa legacy (components/map/rightPanel/MapAlerts.vue:31):
 * solo un `false` EXPLICITO bloquea. Si el atributo no viene, el usuario ve
 * — habria cuentas anteriores a que el permiso existiera y no queremos
 * apagarles el modulo de golpe.
 *
 * @param {Object} user usuario de la sesion (store auth.user)
 * @returns {boolean}
 */
export function canSeeReportants(user) {
  const attrs = (user && user.attributes) || {}
  return readFlag(attrs.permission_reportantes) !== false
}

/**
 * Limpia del estado persistido lo que el operador ya no tiene derecho a ver.
 *
 * El gate de ingesta no alcanza para `kalan:map-v2:selectedItem` y
 * `kalan:map-v2:pinnedItems`: se rehidratan desde localStorage al arrancar,
 * sin pasar por el WS ni por el loader, y el payload del reportante lleva
 * telefono, coordenadas y domicilio. Sin esta purga, a un operador al que se
 * le retira el permiso le sigue apareciendo el ultimo reportante que tuvo
 * abierto o pineado.
 *
 * Funcion pura: devuelve el estado a aplicar, no muta el recibido.
 *
 * @param {{selectedItem: ?Object, pinnedItems: ?Array}} state estado persistido
 * @param {boolean} allowed resultado de canSeeReportants(user)
 * @returns {{selectedItem: ?Object, pinnedItems: Array}}
 */
export function purgeReportantState(state, allowed) {
  const selectedItem = (state && state.selectedItem) || null
  const pinnedItems = (state && state.pinnedItems) || []
  if (allowed) return { selectedItem, pinnedItems }
  return {
    selectedItem: (selectedItem && selectedItem.entityType === 'reportant') ? null : selectedItem,
    pinnedItems: pinnedItems.filter((p) => !p || p.entityType !== 'reportant'),
  }
}

/**
 * ¿Debe pedirse el histórico de reportes del 9-1-1
 * (`GET /incidents/reports/range`, consumido por el panel de KPIs)?
 *
 * No existe un permiso de incidentes en `user.attributes` — GET /session
 * publica permission_murphy / permission_reportantes / permission_sinaloaSOS
 * y nada más —, así que el criterio con el que el backend filtra /incidents
 * nunca viaja al cliente. Lo que sí llega es el resultado: a una cuenta sin
 * acceso, /incidents responde 200 con cero registros. Se usa esa respuesta
 * como señal de autorización.
 *
 * Es una mitigación temporal: /incidents/reports/range entrega hoy el
 * histórico completo —con teléfonos y nombres de ciudadanos— a cualquier
 * sesión válida, y eso solo se cierra de verdad en el backend. Ver
 * docs/seguridad/2026-09-15-fuga-incidents-reports-range.md
 *
 * Fail-closed: mientras la carga inicial no haya respondido no se pide nada.
 * El operador autorizado que abra el panel en los primeros segundos lo repuebla
 * con el botón de refrescar.
 *
 * @param {{loaded: boolean, backendCount: number}} access resultado de la
 *   carga inicial de /incidents. `backendCount` es el conteo CRUDO que
 *   devolvió el backend, antes de los filtros de estado/antigüedad/geocerca
 *   del cliente: esos filtran por operación, no por autorización.
 * @returns {boolean}
 */
export function canSeeIncidentReports(access) {
  if (!access || access.loaded !== true) return false
  return Number(access.backendCount) > 0
}

/**
 * Quita el bloque de incidentes del KPI persistido.
 *
 * El summary del panel se guarda entero en `kalan:map-v2:selectedItem`, y su
 * bloque `incidents` lleva las categorías del 9-1-1 y el array crudo de
 * incidentes. Mismo criterio que `murphyResult`: datos personales viven en
 * memoria, no en localStorage. El resto del summary (cámaras, unidades,
 * sensores, LPR, CPR) se conserva, así que el panel sigue abriendo con su
 * contenido no sensible y recupera los incidentes al refrescar.
 *
 * Función pura: devuelve el item a aplicar, no muta el recibido.
 *
 * @param {?Object} item selectedItem persistido
 * @returns {?Object}
 */
export function stripKpiIncidents(item) {
  if (!item || item.entityType !== 'kpi') return item
  const summary = item.data && item.data.data
  if (!summary || !summary.incidents) return item
  const cleanSummary = { ...summary }
  delete cleanSummary.incidents
  return { ...item, data: { ...item.data, data: cleanSummary } }
}

// ── Funciones del mapa V2 gobernadas por accesos de perfil ───────────────
// `accesosPorPerfil` lo pone usuarioEfectivo (util/accesos-perfil.js) solo
// cuando PERMISOS_POR_PERFIL está activo. Sin él, cada función se comporta
// como antes de existir su permiso, para no cambiar nada al desplegar.

function porPerfil(user) {
  return !!(user && user.accesosPorPerfil)
}

function flagEstricto(user, clave) {
  return readFlag(user && user.attributes && user.attributes[clave]) === true
}

/** Murphy: hoy solo lo bloquea un `false` explícito (paridad con Map.vue). */
export function canUseMurphy(user) {
  if (porPerfil(user)) return flagEstricto(user, 'permission_murphy')
  const attrs = (user && user.attributes) || {}
  return readFlag(attrs.permission_murphy) !== false
}

/** Radio TETRA: permiso nuevo; antes del perfil no se validaba. */
export function canUseRadio(user) {
  return porPerfil(user) ? flagEstricto(user, 'permission_radio') : true
}

/** Controles PTZ: en Map V2 no se validaban (el legacy sí, con ptz_control). */
export function canUsePtz(user) {
  return porPerfil(user) ? flagEstricto(user, 'ptz_control') : true
}

/**
 * Botón de búsqueda facial. El panel ya exige permission_face_search
 * (SmartSearchPanel.canFaceSearch) con pase de administrador; con perfil,
 * además se oculta el botón.
 */
export function canUseFaceSearch(user) {
  if (!porPerfil(user)) return true
  if (user.administrator === true) return true
  return flagEstricto(user, 'permission_face_search')
}
